import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { CheckCircle, XCircle, Award, RotateCcw, Loader2 } from "lucide-react";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { useSubmitQuiz, useBestScore, QuizAttemptResult } from "@/hooks/use-quiz";

// Local quiz question type for this component
export interface LocalQuizQuestion {
  id: string;
  question: string;
  options: string[];
  correctAnswer: number; // index of correct answer
  explanation?: string;
}

interface QuizComponentProps {
  contentId: string;
  strengthName: string;
  questions: LocalQuizQuestion[];
  passingScore?: number; // percentage (default 70)
  onComplete?: (score: number, passed: boolean, attemptData?: QuizAttemptResult) => void;
}

export const QuizComponent = ({
  contentId,
  strengthName,
  questions,
  passingScore = 70,
  onComplete,
}: QuizComponentProps) => {
  const { t } = useTranslation();
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState<Record<string, number>>({});
  const [showResults, setShowResults] = useState(false);
  const [showExplanation, setShowExplanation] = useState(false);
  const [startTime, setStartTime] = useState<number>(Date.now());
  const [quizResult, setQuizResult] = useState<QuizAttemptResult | null>(null);

  // API hooks
  const { mutate: submitQuiz, isPending: isSubmitting } = useSubmitQuiz(contentId);
  const { data: bestScore } = useBestScore(contentId);

  const currentQuestion = questions[currentQuestionIndex];
  const totalQuestions = questions.length;
  const progress = ((currentQuestionIndex + 1) / totalQuestions) * 100;
  const isLastQuestion = currentQuestionIndex === totalQuestions - 1;
  const hasAnswered = selectedAnswers[currentQuestion?.id] !== undefined;

  // Initialize start time
  useEffect(() => {
    setStartTime(Date.now());
  }, []);

  const handleAnswerSelect = (answerIndex: number) => {
    if (showResults) return; // Don't allow changes after completion

    setSelectedAnswers({
      ...selectedAnswers,
      [currentQuestion.id]: answerIndex,
    });
    setShowExplanation(false);
  };

  const handleNext = () => {
    if (isLastQuestion) {
      calculateAndShowResults();
    } else {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      setShowExplanation(false);
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
      setShowExplanation(false);
    }
  };

  const handleShowExplanation = () => {
    setShowExplanation(true);
  };

  const calculateAndShowResults = () => {
    // Calculate time spent in seconds
    const timeSpent = Math.floor((Date.now() - startTime) / 1000);

    // Convert selected answers to API format
    // Find question index by mapping question id back to original array
    const answersPayload = questions.map((q, index) => {
      const selectedIndex = selectedAnswers[q.id];
      // Convert index (0,1,2,3) to letter (A,B,C,D)
      const selectedLetter = String.fromCharCode(65 + selectedIndex);
      return {
        questionIndex: index,
        selectedAnswer: selectedLetter,
      };
    });

    // Submit quiz to backend
    submitQuiz(
      { answers: answersPayload, timeSpent },
      {
        onSuccess: (data) => {
          setQuizResult(data);
          setShowResults(true);
          onComplete?.(data.score, data.passed, data);
        },
        onError: (error) => {
          console.error("Quiz submission failed:", error);
          // Fallback to local calculation if API fails
          let correctCount = 0;
          questions.forEach((q) => {
            if (selectedAnswers[q.id] === q.correctAnswer) {
              correctCount++;
            }
          });
          const localScore = Math.round((correctCount / totalQuestions) * 100);
          const localPassed = localScore >= passingScore;
          onComplete?.(localScore, localPassed);
        },
      }
    );
  };

  const handleRetake = () => {
    setSelectedAnswers({});
    setCurrentQuestionIndex(0);
    setShowResults(false);
    setQuizResult(null);
    setShowExplanation(false);
    setStartTime(Date.now());
  };

  const getAnswerStatus = (questionId: string, answerIndex: number) => {
    if (!showResults || !quizResult) return null;

    const question = questions.find((q) => q.id === questionId);
    if (!question) return null;

    const questionIndex = questions.findIndex((q) => q.id === questionId);
    const resultAnswer = quizResult.answers.find((a) => a.questionIndex === questionIndex);

    if (!resultAnswer) {
      // Fallback to local comparison
      const selectedAnswer = selectedAnswers[questionId];
      const isCorrect = question.correctAnswer === answerIndex;
      const isSelected = selectedAnswer === answerIndex;

      if (isCorrect) return "correct";
      if (isSelected && !isCorrect) return "incorrect";
      return null;
    }

    const correctAnswerIndex = resultAnswer.correctAnswer.charCodeAt(0) - 65;
    const selectedAnswerIndex = resultAnswer.selectedAnswer.charCodeAt(0) - 65;

    if (correctAnswerIndex === answerIndex) return "correct";
    if (selectedAnswerIndex === answerIndex) return "incorrect";
    return null;
  };

  if (!currentQuestion) {
    return (
      <Card className="p-4 text-center sm:p-6">
        <p className="text-muted-foreground">{t('assessment.noQuestions')}</p>
      </Card>
    );
  }

  if (showResults && quizResult) {
    return (
      <Card className="p-4 bg-card/50 backdrop-blur-sm border-border/50 sm:p-6">
        <div className="text-center space-y-6">
          <div className="inline-block p-4 rounded-full bg-primary/10">
            <Award className={`w-16 h-16 ${quizResult.passed ? 'text-accent' : 'text-muted-foreground'}`} />
          </div>

          <div>
            <h3 className="text-xl font-bold mb-2 sm:text-2xl">
              {quizResult.passed ? t('assessment.quizPassed') : t('assessment.quizFailed')}
            </h3>
            <p className="text-muted-foreground">
              {t('assessment.yourScore')}: <span className="font-bold text-foreground">{quizResult.score}%</span>
            </p>
            <p className="text-sm text-muted-foreground">
              {quizResult.correctAnswers} {t('assessment.outOf')} {quizResult.totalQuestions} {t('assessment.correct')}
            </p>
            {bestScore && quizResult.score > bestScore.score && (
              <Badge className="mt-2 bg-accent/20 text-accent">
                🎉 New Best Score! (Previous: {bestScore.score}%)
              </Badge>
            )}
            <div className="flex flex-wrap items-center justify-center gap-3 mt-2 text-sm text-muted-foreground sm:gap-4">
              <span>Attempt #{quizResult.attemptNumber}</span>
            </div>
          </div>

          <div className="space-y-4">
            <Button onClick={handleRetake} variant="outline" className="gap-2">
              <RotateCcw className="w-4 h-4" />
              {t('assessment.retakeQuiz')}
            </Button>

            <div className="text-left space-y-4 pt-6 border-t">
              <h4 className="font-semibold">{t('assessment.reviewAnswers')}</h4>
              {quizResult.answers.map((answer, index) => {
                return (
                  <div key={index} className="space-y-2">
                    <div className="flex items-start gap-2">
                      {answer.isCorrect ? (
                        <CheckCircle className="w-5 h-5 text-accent mt-0.5 flex-shrink-0" />
                      ) : (
                        <XCircle className="w-5 h-5 text-destructive mt-0.5 flex-shrink-0" />
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="break-words font-medium">
                          {index + 1}. {answer.question}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {t('assessment.yourAnswer')}: {answer.selectedAnswer}
                        </p>
                        {!answer.isCorrect && (
                          <p className="text-sm text-accent">
                            {t('assessment.correctAnswer')}: {answer.correctAnswer}
                          </p>
                        )}
                        {answer.explanation && (
                          <p className="text-sm text-muted-foreground mt-1 italic">
                            {answer.explanation}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-4 bg-card/50 backdrop-blur-sm border-border/50 sm:p-6">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <Badge variant="outline" className="text-sm">
            {t('assessment.question')} {currentQuestionIndex + 1} / {totalQuestions}
          </Badge>
          <span className="text-sm text-muted-foreground">
            {strengthName}
          </span>
        </div>

        {/* Progress */}
        <Progress value={progress} className="h-2" />

        {/* Question */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold leading-relaxed sm:text-xl">
            {currentQuestion.question}
          </h3>

          <RadioGroup
            value={selectedAnswers[currentQuestion.id]?.toString()}
            onValueChange={(value) => handleAnswerSelect(parseInt(value))}
            className="space-y-3"
          >
            {currentQuestion.options.map((option, index) => {
              const status = getAnswerStatus(currentQuestion.id, index);

              return (
                <div
                  key={index}
                  className={`flex min-w-0 items-center gap-3 p-3 rounded-lg border transition-all sm:p-4 ${
                    selectedAnswers[currentQuestion.id] === index
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-primary/50'
                  } ${
                    status === 'correct' ? 'border-accent bg-accent/5' : ''
                  } ${
                    status === 'incorrect' ? 'border-destructive bg-destructive/5' : ''
                  }`}
                >
                  <RadioGroupItem value={index.toString()} id={`option-${index}`} />
                  <Label
                    htmlFor={`option-${index}`}
                    className="min-w-0 flex-1 cursor-pointer break-words font-normal"
                  >
                    {option}
                  </Label>
                  {status === 'correct' && (
                    <CheckCircle className="w-5 h-5 text-accent" />
                  )}
                  {status === 'incorrect' && (
                    <XCircle className="w-5 h-5 text-destructive" />
                  )}
                </div>
              );
            })}
          </RadioGroup>

          {/* Explanation */}
          {showExplanation && currentQuestion.explanation && (
            <div className="p-4 bg-muted/50 rounded-lg border border-border">
              <p className="text-sm text-muted-foreground">
                <span className="font-semibold text-foreground">{t('assessment.explanation')}: </span>
                {currentQuestion.explanation}
              </p>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-3 pt-4 border-t sm:flex-row sm:items-center sm:justify-between">
          <Button
            variant="outline"
            onClick={handlePrevious}
            disabled={currentQuestionIndex === 0}
          >
            {t('common.previous')}
          </Button>

          <div className="flex flex-col gap-2 sm:flex-row">
            {hasAnswered && currentQuestion.explanation && !showExplanation && (
              <Button variant="ghost" onClick={handleShowExplanation}>
                {t('assessment.showExplanation')}
              </Button>
            )}
            <Button
              onClick={handleNext}
              disabled={!hasAnswered || isSubmitting}
              className="gap-2"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>{isLastQuestion ? t('assessment.submitQuiz') : t('common.next')}</>
              )}
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
};
